import cuid from "cuid";
import { filter, get, map } from "lodash";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery } from "shared/hooks/useApi";
import { NAME_SPACES } from "shared/locales/constants";
import graphql from "utils/api/graphql";
import { CREATE_ATTACHMENT } from "utils/api/graphql/mutations/attachment";
import { KEYS, ATTACHMENT_TYPES } from "utils/constants";
import Template from "../Template";
import View from "./View";

export const DOCUMENTS_TYPE = {
  payerCin: ATTACHMENT_TYPES.PAYER_CIN,
  payerRib: ATTACHMENT_TYPES.PAYER_RIB,
  contributionPaymentAgreement: ATTACHMENT_TYPES.CONTRIBUTION_PAYMENT_AGREEMENT,
};

const PayerJustification = ({
  currentStatus,
  tracer,
  project,
  onBack,
  updateUrl,
  onNext,
}) => {
  const [initialValues, setInitialValues] = useState(null);
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation(NAME_SPACES.PUBLIC.PROCESSES);
  const TRANSLATION = t("B2C_LMF.PAYER_JUSTIFICATION", {
    returnObjects: true,
  });
  const subscriptionId =
    get(project, "fields.currentSubscription") ||
    localStorage.getItem(KEYS.SUBSCRIPTION_ID);

  const { data: subscription } = useQuery(graphql.queries.SUBSCRIPTION, {
    variables: { where: { id: subscriptionId } },
  });

  const adherentId = get(project, "contact.user.id");

  const { loading: attachmentLoading } = useQuery(graphql.queries.ATTACHMENTS, {
    fetchPolicy: "no-cache",
    variables: {
      where: {
        AND: [
          { subscription: { id: subscriptionId } },
          { user: { id: adherentId } },
        ],
      },
      isIn: {
        type: Object.values(DOCUMENTS_TYPE),
      },
    },

    onCompleted: ({ attachments: { data: attachmentsList } }) => {
      setInitialValues({
        payerCin: map(
          filter(
            attachmentsList,
            ({ type }) => type === ATTACHMENT_TYPES.PAYER_CIN,
          ),
          ({ id, name, fileUrl }) => ({
            uid: id,
            name,
            status: "done",
            url: fileUrl,
          }),
        ),
        contributionPaymentAgreement: map(
          filter(
            attachmentsList,
            ({ type }) =>
              type === ATTACHMENT_TYPES.CONTRIBUTION_PAYMENT_AGREEMENT,
          ),
          ({ id, name, fileUrl }) => ({
            uid: id,
            name,
            status: "done",
            url: fileUrl,
          }),
        ),
        payerRib: map(
          filter(
            attachmentsList,
            ({ type }) => type === ATTACHMENT_TYPES.PAYER_RIB,
          ),
          ({ id, name, fileUrl }) => ({
            uid: id,
            name,
            status: "done",
            url: fileUrl,
          }),
        ),
      });
      setLoading(false);
    },
  });
  const [addAttachment] = useMutation(CREATE_ATTACHMENT);

  const onSubmit = async ({ includedChildren, ...documents }) => {
    setLoading(true);
    const documentsToUpload = [];
    if (!adherentId) {
      return;
    }

    Object.entries(documents)?.map(([key, attachments]) =>
      attachments
        ?.filter((e) => !e.status)
        .forEach(({ name, contentType, base64 }) => {
          documentsToUpload.push({
            id: cuid(),
            subscription: { id: subscriptionId },
            user: { id: adherentId },
            name,
            content: base64,
            contentType,
            type: DOCUMENTS_TYPE[key],
          });
        }),
    );
    await Promise.all(
      documentsToUpload.map((document, index) =>
        addAttachment({
          variables: {
            data: document,
          },
        }),
      ),
    );
    const source = localStorage.getItem(KEYS.SOURCE);
    updateUrl({
      subscriptionId,
      url: get(
        subscription,
        "subscription.insurancePlan.config.generatedUrl",
        `${process.env.REACT_APP_BASE_URL}/#/public/process/init/${source}`,
      ),
    });
    onNext({
      payload: {},
    });
  };

  return (
    <Template
      progress={currentStatus.progress}
      onBack={onBack}
      loading={loading || attachmentLoading || !initialValues}
      View={
        initialValues && (
          <View
            key={JSON.stringify(initialValues)}
            locked={get(subscription, "subscription.locked", false)}
            onSubmit={onSubmit}
            initialValues={initialValues}
            translate={TRANSLATION}
          />
        )
      }
      HeaderComponent={{
        subscription: get(subscription, "subscription"),
        tracer,
        project,
        contact: get(project, "contact"),
      }}
      contact={get(project, "contact")}
    />
  );
};
export default PayerJustification;
